<template>
  <div>
    <PageHeaderBanner>
      <template #title>円転後仕入価格帯別係数設定</template>
    </PageHeaderBanner>
    <div class="cmd01200-form-area cmd01200-form">
      <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
      <q-form
        class="q-mt-md"
        autofocus
        greedy
        novalidate
        @submit.prevent="checkData"
        @reset="reset"
        @validation-error="displayErrMsg"
      >
        <table style="padding-right: 57px; margin: 0 auto">
          <tr>
            <th colspan="2" style="padding-left: 80px">下限</th>
            <th>上限</th>
            <th style="padding-left: 40px">係数</th>
          </tr>
          <tr v-for="(data, index) in inputData" :key="index">
            <td v-if="Number(index) < 9" style="width: 80px">区分 {{ Number(index) + 1 }}:</td>
            <td v-if="Number(index) >= 9" style="width: 80px">区分{{ Number(index) + 1 }}:</td>
            <td>
              <div class="q-gutter-sm row items-center q-mr-md">
                <label
                  v-if="Number(index) === 0"
                  class="text-right"
                  style="width: 200px; height: 21px; padding-right: 0.5rem"
                  v-text="0"
                ></label>
                <q-input
                  v-if="Number(index) !== 0"
                  v-model="data.lower_price_limit"
                  outlined
                  clearable
                  dense
                  style="width: 200px"
                  maxlength="10"
                  input-class="text-right"
                  class="input-area exception-right"
                  :error="data.lowErrFlg"
                  :rules="[
                    (val) =>
                      !isEmpty(val) ||
                      (isNumeric(val)
                        ? (Number(val) > 0 && Number(val) < 100000000) ||
                          $t('validation.betweenMoney', { param1: '1', param2: '99,999,999' })
                        : isMoneyType(val) ||
                          $t('validation.betweenMoney', { param1: '1', param2: '99,999,999' })),
                  ]"
                  lazy-rules
                  @keydown.enter.prevent
                />
                <label>円以上</label>
                <label style="padding-left: 10px; padding-right: 10px">～</label>
              </div>
            </td>
            <td>
              <div class="q-gutter-sm row items-center q-mr-md">
                <q-input
                  v-model="data.upper_price_limit"
                  outlined
                  clearable
                  dense
                  style="width: 200px"
                  maxlength="10"
                  input-class="text-right"
                  class="input-area exception-right"
                  :error="data.upErrFlg"
                  :rules="[
                    (val) =>
                      !isEmpty(val) ||
                      (isNumeric(val)
                        ? (Number(val) > 0 && Number(val) < 100000000) ||
                          $t('validation.betweenMoney', { param1: '1', param2: '99,999,999' })
                        : isMoneyType(val) ||
                          $t('validation.betweenMoney', { param1: '1', param2: '99,999,999' })),
                    (val) =>
                      Number(index) === 0 ? isEmpty(val) || $t('validation.required') : true,
                  ]"
                  lazy-rules
                  @keydown.enter.prevent
                />
                <label>円未満</label>
              </div>
            </td>
            <td style="padding-left: 40px">
              <div class="q-gutter-sm row items-center q-mr-md">
                <q-input
                  v-model="data.coefficient"
                  outlined
                  clearable
                  dense
                  style="width: 200px"
                  maxlength="6"
                  input-class="text-right"
                  class="input-area exception-right"
                  :error="data.coefErrFlg"
                  :rules="[
                    (val) => (Number(index) ? isEmpty(val) || $t('validation.required') : true),
                    (val) => Number(val) >= 0.01 || '0.01以上で入力してください',
                    (val) =>
                      !isEmpty(val) ||
                      isTwoDecimalPlaces(val) ||
                      $t('validation.two_decimal_places'),
                    (val) =>
                      !isEmpty(val) ||
                      Number(val) < 1000 ||
                      $t('validation.max_integer', { param1: '3' }),
                  ]"
                  lazy-rules
                  @keydown.enter.prevent
                />
              </div>
            </td>
          </tr>
        </table>
        <FormFooterBanner style="position: inherit">
          <template #button-area>
            <div class="q-gutter-x-xs flex justify-end cmd01200-button-area">
              <q-btn type="submit" color="primary" style="width: 150px" :label="'変更'" />
              <q-btn
                type="reset"
                outline
                style="width: 150px; background: var(--q-white) !important; color: var(--q-delete)"
                :icon="'cached'"
                :label="'リセット'"
              />
            </div>
          </template>
        </FormFooterBanner>
        <AlertDialog
          :display="dialogDisplayFlg"
          :level="DIALOG_LEVEL"
          :confirmation="dialogConfFlg"
          :title="dialogTitle"
          :text="dialogText"
          @complete="getCoefficients()"
          @confirm="editCoefficients()"
          @close="closeDialog()"
        />
      </q-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import axios from '../../common/axios-common';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import MessageChip from '../../components/chip/MessageChip.vue';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import {
  isEmpty,
  isNumeric,
  isTwoDecimalPlaces,
  isMoneyType,
} from '../../common/utils/validateUtils';
import messages from '../../messages/message.json';

const router = useRouter();
// ダイアログのdisplayFlg(表示判断)
const dialogDisplayFlg = ref(false);
// ダイアログのlevel(informationで固定)
const DIALOG_LEVEL = 'information';
// ダイアログのtitle
const dialogTitle = ref('');
// ダイアログのtext
const dialogText = ref(['']);
// ダイアログのconfirmation(trueならキャンセルボタンを表示しない)
const dialogConfFlg = ref(false);
// エラーメッセージ表示フラグ
const isMessageChip = ref(false);
// エラーメッセージ
const errMessage = ref('');

// リセット用データ
const resetData = reactive([
  {
    lower_price_limit: '',
    upper_price_limit: '',
    coefficient: '',
    isAllEmpty: false,
    lowErrFlg: false,
    upErrFlg: false,
    coefErrFlg: false,
  },
]);

// 入力データ
const inputData = ref({ ...resetData });

/**
 * エラーメッセージ表示
 */
const displayErrMsg = () => {
  errMessage.value = messages.ja.message.MSG_50070E;
  isMessageChip.value = true;
  // TOPにスクロール
  setTimeout(() => window.scrollTo({ top: 0 }), 400);
};

/**
 * エラーチップを初期化
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
const resetError = async () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

/**
 * エラー初期化
 */
const resetErrors = () => {
  // メッセージチップを非表示にし、メッセージを初期化
  errMessage.value = '';
  isMessageChip.value = false;

  // 全区分の全項目を白に変更
  for (let index = 0; index < 20; index += 1) {
    inputData.value[index].lowErrFlg = false;
    inputData.value[index].upErrFlg = false;
    inputData.value[index].coefErrFlg = false;
  }
};

/**
 * 下限、上限からカンマを取り除く処理
 */
const removeComma = () => {
  for (let index = 0; index < 20; index += 1) {
    const low = inputData.value[index].lower_price_limit;
    const up = inputData.value[index].upper_price_limit;

    if (low !== null && low !== '') {
      // 下限価格からカンマを取り除く
      inputData.value[index].lower_price_limit = low.split(',').join('');
    }
    if (up !== null && up !== '') {
      // 上限価格からカンマを取り除く
      inputData.value[index].upper_price_limit = up.split(',').join('');
    }
  }
};

/**
 * 下限、上限にカンマを付け、係数の小数部を2桁にする処理
 */
const attachComma = () => {
  for (let index = 0; index < 20; index += 1) {
    const low = inputData.value[index].lower_price_limit;
    const up = inputData.value[index].upper_price_limit;
    const coef = inputData.value[index].coefficient;

    if (low !== null && low !== '') {
      // 下限価格にカンマを付ける
      inputData.value[index].lower_price_limit = Number(low).toLocaleString();
    } else {
      inputData.value[index].lower_price_limit = '';
    }
    if (up !== null && up !== '') {
      // 上限価格にカンマを付ける
      inputData.value[index].upper_price_limit = Number(up).toLocaleString();
    } else {
      inputData.value[index].upper_price_limit = '';
    }
    if (coef !== null && coef !== '') {
      // 係数の小数部を2桁に固定する
      inputData.value[index].coefficient = Number(coef).toFixed(2);
    } else {
      inputData.value[index].coefficient = '';
    }
  }
};

/**
 * 入力値リセット
 */
const reset = () => {
  resetErrors();
  for (let index = 0; index < 20; index += 1) {
    inputData.value[index] = { ...resetData[index] };
  }
  // 下限と上限にカンマをつけ、係数の小数部を2桁にする
  attachComma();
  // TOPにスクロール
  setTimeout(() => window.scrollTo({ top: 0 }), 400);
};

/**
 * 入力エラーがない場合にダイアログの中身を設定、表示する処理
 */
const submit = () => {
  // 変更確認ダイアログのtitle、textを設定
  dialogTitle.value = '更新確認';
  dialogText.value = ['入力された内容で更新します。', 'よろしいですか？'];
  // ダイアログを表示
  dialogDisplayFlg.value = true;
};

/**
 * ダイアログのキャンセルボタン押下時の処理
 */
const closeDialog = () => {
  dialogDisplayFlg.value = false;
  // TOPにスクロール
  setTimeout(() => window.scrollTo({ top: 0 }), 400);
};

/**
 * 初期表示処理
 */
const getCoefficients = async () => {
  // Loading Start
  Loading.show();
  axios
    .post('/cmd012001/getCoefficients')
    .then((res) => {
      const { common, coefficientList } = res.data;

      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // リセット用データを初期化後、取得した値を格納
          resetData.splice(0);
          Object.assign(resetData, coefficientList);

          // 要素数が20になるまで配列の要素を追加する
          for (let index = coefficientList.length; index < 20; index += 1) {
            resetData.push({
              lower_price_limit: '',
              upper_price_limit: '',
              coefficient: '',
              isAllEmpty: false,
              lowErrFlg: false,
              upErrFlg: false,
              coefErrFlg: false,
            });
          }

          for (let index = 0; index < 20; index += 1) {
            inputData.value[index] = { ...resetData[index] };
          }

          // 下限と上限にカンマをつけ、係数の小数部を2桁にする
          attachComma();
          // TOPにスクロール
          setTimeout(() => window.scrollTo({ top: 0 }), 400);
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // ダイアログを非表示にし、キャンセルボタン表示判断をfalseに
      dialogDisplayFlg.value = false;
      dialogConfFlg.value = false;
      // Loading End
      Loading.hide();
    });
};

/**
 * 円転後仕入価格と係数の変更処理
 */
const editCoefficients = async () => {
  // Loading Start
  Loading.show();
  // 下限、上限からカンマを取り除く
  removeComma();

  // 更新用データセット
  const formData = new FormData();
  for (let index = 0; index < 20; index += 1) {
    if (inputData.value[index].isAllEmpty) {
      break;
    }
    formData.append(
      `editCoefficientList[${index}][lower_price_limit]`,
      index === 0 ? '0' : inputData.value[index].lower_price_limit
    );
    formData.append(
      `editCoefficientList[${index}][upper_price_limit]`,
      inputData.value[index].upper_price_limit
    );
    formData.append(
      `editCoefficientList[${index}][coefficient]`,
      Number(inputData.value[index].coefficient).toString()
    );
  }

  // 円転後仕入価格と係数の変更を行うController呼び出し
  await axios
    .post('/cmd012001/editCoefficients', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 下限と上限にカンマをつけ、係数の小数部を2桁にする
          attachComma();
          // 変更確認ダイアログのtitle、textを設定し、キャンセルボタン表示判断をtrueに
          dialogTitle.value = '更新完了';
          dialogText.value = ['更新が完了しました。'];
          dialogConfFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.UPDATE_ERROR:
              // 下限と上限にカンマをつけ、係数の小数部を2桁にする
              attachComma();
              // ダイアログを非表示
              dialogDisplayFlg.value = false;
              // メッセージチップにエラーメッセージを表示
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;
            case DetailResultStatusCode.PARAMETER_ERROR:
              // ダイアログを非表示
              dialogDisplayFlg.value = false;
              // メッセージチップにエラーメッセージを表示
              errMessage.value = common.message;
              isMessageChip.value = true;
              setTimeout(() => window.scrollTo({ top: 0 }), 400);
              break;
            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 入力値のエラーチェック
 */
const checkData = () => {
  resetErrors();
  // 下限、上限からカンマを取り除く
  removeComma();
  // 入力データの配列を逆から回し、入力があった時点でtrue
  const inputFlg = ref(false);

  for (let index = 19; index > 0; index -= 1) {
    const low = inputData.value[index].lower_price_limit;
    const up = inputData.value[index].upper_price_limit;
    const coef = inputData.value[index].coefficient;

    if (
      (low === null || low === '') &&
      (up === null || up === '') &&
      (coef === null || coef === '')
    ) {
      // 全項目が未入力の区分
      inputData.value[index].isAllEmpty = true;
    } else {
      // 1項目でも入力されている区分
      inputData.value[index].isAllEmpty = false;
      inputFlg.value = true;
    }

    // 1項目でも入力のある区分以前に全項目未入力の区分がないかチェック
    if (inputFlg.value && inputData.value[index].isAllEmpty) {
      // 入力エラーの条件に該当する場合、エラーメッセージを表示
      errMessage.value = messages.ja.message.MSG_50061E;
      isMessageChip.value = true;
      // 入力エラーの条件に該当する区分の全項目を赤く表示
      inputData.value[index].lowErrFlg = true;
      inputData.value[index].upErrFlg = true;
      inputData.value[index].coefErrFlg = true;
    }
  }

  // ここまでの入力値チェックでエラーがなかった場合のみ、この処理を行う
  if (!isMessageChip.value) {
    for (let index = 1; index < 20; index += 1) {
      // チェックを行う区分の各データを変数にセット
      const low = inputData.value[index].lower_price_limit;
      const up = inputData.value[index].upper_price_limit;
      const coef = inputData.value[index].coefficient;

      // 区分ごとに全項目が入力されているかチェック
      if (!inputData.value[index].isAllEmpty) {
        const noDataFlg = ref(false);
        // 入力エラーの条件に該当する項目を赤く表示
        if (low === null || low === '') {
          inputData.value[index].lowErrFlg = true;
          noDataFlg.value = true;
        }
        if (up === null || up === '') {
          inputData.value[index].upErrFlg = true;
          noDataFlg.value = true;
        }
        if (coef === null || coef === '') {
          inputData.value[index].coefErrFlg = true;
          noDataFlg.value = true;
        }
        // 入力エラーの条件に該当する場合、エラーメッセージを表示
        if (noDataFlg.value) {
          errMessage.value = messages.ja.message.MSG_50062E;
          isMessageChip.value = true;
        }
      }
    }
  }

  // ここまでの入力値チェックでエラーがなかった場合のみ、この処理を行う
  if (!isMessageChip.value) {
    for (let index = 1; index < 20; index += 1) {
      // チェックを行う区分の各データを変数にセット
      const low = inputData.value[index].lower_price_limit;

      if (!inputData.value[index].isAllEmpty) {
        // 入力値の下限と前区分の上限が一致しているかつ、入力値が99999999の場合
        if (
          Number(low) === Number(inputData.value[index - 1].upper_price_limit) &&
          Number(inputData.value[index - 1].upper_price_limit) === 99999999
        ) {
          // 入力エラーの条件に該当する場合、エラーメッセージを表示
          errMessage.value = messages.ja.message.MSG_50063E;
          isMessageChip.value = true;
          // 入力エラーの条件に該当する下限価格を赤く表示
          inputData.value[index].lowErrFlg = true;
          break;
        }

        // 下限価格に前区分の上限価格と同じ金額が入力されているかチェック
        if (Number(low) !== Number(inputData.value[index - 1].upper_price_limit)) {
          // 入力エラーの条件に該当する場合、エラーメッセージを表示
          errMessage.value = messages.ja.message.MSG_50064E;
          isMessageChip.value = true;
          // 入力エラーの条件に該当する下限価格を赤く表示
          inputData.value[index].lowErrFlg = true;
          break;
        }

        // 入力されている最大区分の場合break
        if (index === 19) {
          break;
        }
        if (inputData.value[index + 1].isAllEmpty) {
          break;
        }
        // 上限価格に下限価格を上回る金額が入力されているかチェック
        if (Number(low) >= Number(inputData.value[index].upper_price_limit)) {
          // 入力エラーの条件に該当する場合、エラーメッセージを表示
          errMessage.value = messages.ja.message.MSG_50065E;
          isMessageChip.value = true;
          // 入力エラーの条件に該当する上限価格を赤く表示
          inputData.value[index].upErrFlg = true;
          break;
        }
      }
    }
  }

  // ここまでの入力値チェックでエラーがなかった場合のみ、この処理を行う
  if (!isMessageChip.value) {
    // 区分20の上限価格に99,999,999が入力されているかチェック
    if (!inputData.value[19].isAllEmpty) {
      if (Number(inputData.value[19].upper_price_limit) !== 99999999) {
        // 入力エラーの条件に該当する場合、エラーメッセージを表示
        errMessage.value = messages.ja.message.MSG_50066E;
        isMessageChip.value = true;
        // 入力エラーの条件に該当する上限価格を赤く表示
        inputData.value[19].upErrFlg = true;
      }
    } else {
      for (let index = 1; index < 20; index += 1) {
        // 入力されている最大区分の上限価格に99,999,999が入力されているかチェック
        if (inputData.value[index].isAllEmpty) {
          if (Number(inputData.value[index - 1].upper_price_limit) !== 99999999) {
            // 入力エラーの条件に該当する場合、エラーメッセージを表示
            errMessage.value = messages.ja.message.MSG_50066E;
            isMessageChip.value = true;
            // 入力エラーの条件に該当する上限価格を赤く表示
            inputData.value[index - 1].upErrFlg = true;
          }
          break;
        }
      }
    }
  }

  // 下限と上限にカンマをつけ、係数の小数部を2桁にする
  attachComma();
  if (!isMessageChip.value) {
    // 入力値にエラーがなかった場合、ダイアログを表示する
    submit();
  } else {
    // 入力値にエラーがあった場合、TOPにスクロール
    setTimeout(() => window.scrollTo({ top: 0 }), 400);
  }
};

/**
 * 初期表示処理呼び出し
 */
getCoefficients();
</script>
<style scoped>
.cmd01200-form-area,
.cmd01200-button-area {
  width: 90%;
  max-width: 1530px;
  margin: 0 auto;
}
.cmd01200-button-area {
  margin: 0;
}
.cmd01200-message-area .q-field__messages {
  margin-top: -4px;
}
th {
  text-align: left;
}
td {
  padding-bottom: 24px;
}
td .q-input {
  padding: 0;
}
.text {
  padding: auto;
}
.code {
  margin-left: 0;
}
</style>
<style>
.cmd01200-form-area .q-field__messages > div {
  word-break: keep-all;
  word-wrap: normal;
  overflow-wrap: normal;
}
.cmd01200-form-area .q-field__bottom {
  width: 260px;
  color: var(--q-negative);
}

.cmd01200-form-area .input-area.q-field .q-field__native {
  padding-right: 40px;
}

.cmd01200-form-area .input-area.q-field.exception-right .q-field__native {
  padding-right: 34px;
}

.cmd01200-form-area .input-area.q-field.exception-right.q-field--error .q-field__native {
  padding-right: 64px;
}

.cmd01200-form-area .input-area.q-field.exception-right .q-field__suffix {
  position: absolute;
  right: 12px;
}

.cmd01200-form-area .input-area.q-field.exception-right.q-field--error .q-field__suffix {
  position: absolute;
  right: 40px;
}

.cmd01200-form-area
  .input-area
  .text-negative
  .q-field__append.q-field__marginal
  + .q-field__marginal {
  margin-left: -50px;
}
</style>
